import dayjs from 'dayjs'

// Only the URLs below still get a cache_expiry, and it is a cache KEY, not a buster: pinned to the
// end of the UTC day so the browser reuses one response all day and refetches once at the day
// border. It stays because those responses carry a signed URL that must not outlive the day.
// signed-download-url is pinned to the end of its signing window instead (see below).
//
// Every other GET used to get `cache_expiry=Date.now()`, which made every URL unique. That defeated
// the CORS preflight cache (which is keyed on the full URL), so an OPTIONS preceded virtually every
// GET, to the very URL that then served the GET — see allow-cors.js. The buster existed because the
// API's own cache headers could not be trusted; they now say `no-store` (no-cache-header.js), which
// is the thing that actually stops a response being reused.
const URLS_TO_CACHE_24_HOURS = [
  /plans\/.*\/revisions\/.*\/pdf\?contentDisposition=attachment.*/,
  /plans\/.*\/revisions\/.*\/dwg\?contentDisposition=attachment.*/,
  /documents\/.*\/revisions\/.*\/?contentDisposition=attachment.*/,
  /documents\/.*\/attachments\/.*\?contentDisposition=attachment.*/,
  /tasks\/.*\/files\/.*\?contentDisposition=attachment.*/,
  /tasks\/.*\/files\/.*\?contentDisposition=inline.*/,
  /tasks\/.*\/comments\/.*\/files\/.*\?contentDisposition=attachment.*/,
  /tasks\/.*\/comments\/.*\/files\/.*\?contentDisposition=inline.*/,
]

const SIGNED_DOWNLOAD_URL_REGEX = /pf-files\/.*\/signed-download-url/

// Mirrors STABLE_URL_WINDOW_MS in servsrc/lib/s3/s3-get-signed-download-url.js: the server signs
// the same url for every request in a window and caches it until the window ends, so a key that
// changes at the same border asks again exactly when a new url exists. If the two drift apart, the
// cost is one extra request that returns the url the browser already has.
const SIGNED_DOWNLOAD_URL_WINDOW_MS = 4 * 24 * 60 * 60 * 1000

const getCacheExpiry = (url) => {
  if (SIGNED_DOWNLOAD_URL_REGEX.test(url)) {
    return (Math.floor(Date.now() / SIGNED_DOWNLOAD_URL_WINDOW_MS) + 1) * SIGNED_DOWNLOAD_URL_WINDOW_MS
  }

  return dayjs()
    .utc()
    .endOf('day')
    .toDate()
    .getTime()
}

export default ({ $axios }) => {
  $axios.interceptors.request.use(async (config) => {
    const isUrlToCache = [
      ...URLS_TO_CACHE_24_HOURS,
      SIGNED_DOWNLOAD_URL_REGEX,
    ].find(regex => regex.test(config.url))

    if (config.method !== 'get' || !isUrlToCache) {
      return config
    }

    config.params = config.params || {}

    /**
      * ATTENTION: We don't do the cache handling for Cypress tests right now.
      * Cypress clears the browser cache only between files, not between tests.
      * This means, that if we are intercepting download requests, this can lead to bugs where we wait for requests which are actually cached.
      */
    if (window.Cypress) {
      config.params.cache_expiry = new Date()
    } else {
      config.params.cache_expiry = getCacheExpiry(config.url)
    }

    return config
  })
}
